import {
  CompanyFilter,
  InclusionMethod,
} from "../../legacy/types/companyFilter";
import { ExportInclusionData } from "../../pages/Workspace/ExportModal/types";
import { getTenantId, UserProfile } from "../../utils/auth";
import { RadioOption } from "../RadioOptionsInput";
import {
  EXPORT_DATA_TYPES,
  EXPORT_INCLUSION_TYPES,
  EXPORT_RELATED_FILTERS,
  ExportPayload,
  ExtraSearchFilters,
} from "./types";
import { MatchFileConfig } from "../../types/workspace";
import {
  ProductPresenceColumn,
  VendorPresenceColumn,
} from "./SpendSheet/types";
import type {
  ModelScoreFilterInput,
  OpportunityGeneratorScoringProfileSalesplaySearch,
} from "@/generated/company-search";

export const getCommonExportRatioOptions = (
  type: EXPORT_DATA_TYPES | string
): RadioOption[] => {
  return [
    {
      label: `Include all ${type}`,
      value: EXPORT_INCLUSION_TYPES.ALL,
    },
    {
      label: `Do not include any ${type}`,
      value: EXPORT_INCLUSION_TYPES.NONE,
    },
  ];
};

export const getFilterRadioOption = (
  type: EXPORT_DATA_TYPES,
  filterName: EXPORT_RELATED_FILTERS,
  isFilterEmpty: boolean
): RadioOption => {
  const tooltip = isFilterEmpty
    ? `Please add ${filterName} filters to your search in order to see specific ${type} in your exports`
    : undefined;

  const label =
    type === EXPORT_DATA_TYPES.INSTALL
      ? "Match the Tech Install filters (Product Categories, Vendors, Products) selected"
      : `Only include ${type} that matches the ${filterName} filters selected`;

  return {
    value: EXPORT_INCLUSION_TYPES.FILTERS,
    label,
    tooltipAsBubble: true,
    tooltipPosition: "right",
    disabled: isFilterEmpty,
    tooltip,
  };
};

const baseFilters: CompanyFilter = {
  productAttributes: {
    ids: [],
  },
  companyMatch: undefined,
  hgIds: [],
  hgIdsIncludingCorporateRelatives: [],
  fuzzySearch: "",
  industry: undefined,
  countryAlpha2s: [],
  regionNames: [],
  stateIds: [],
  excludedCountryAlpha2s: [],
  globalPresence: {
    isMultiNational: false,
    subsidiaryLocations: undefined,
  },
  isGu: undefined,
  isDu: undefined,
  isGhq: undefined,
  isGp: undefined,
  isDp: undefined,
  intent: {
    topics: {
      ids: [],
      inclusionMethod: InclusionMethod.ALL_PRESENT,
    },
    buyersJourneyIds: [],
    signalScore: "HIGH",
    contextTypeIds: [],
    location: {
      countryAlpha2s: null,
      regionNames: null,
      stateIds: null,
      excludedCountryAlpha2s: null,
    },
    numberOfCadences: 4,
  },
  vendors: [
    {
      ids: [],
      roleIds: [],
      departmentIds: [],
      installAge: null,
      installAgeMonths: null,
      inclusionMethod: InclusionMethod.ALL_PRESENT,
    },
  ],
  products: [
    {
      ids: [22934],
      type: "manual",
      inclusionMethod: InclusionMethod.ALL_PRESENT,
    },
  ],
  productCategories: {
    ids: [],
    roleIds: [],
    departmentIds: [],
    installAge: null,
    installAgeMonths: null,
  },
  departmentIds: [],
  roleIds: [],
  includeDecisionMakersAndInfluencersOnly: false,
};
//todo : correct this code once all export types are clear,as of now its not derived from schema types from io-ts
export const baseExportPayload: ExportPayload = {
  exportType: "companyList",
  format: "XLSX",
  profile: {
    email: "",
    oktaUserId: "",
  },
  params: {
    searchParams: {
      sort: [
        {
          field: "revenue_range_min",
          direction: "DESC",
        },
        {
          field: "hg_id",
          direction: "ASC",
        },
      ],
      filters: baseFilters,
      scoringProfiles: [],
      localizeInstalls: true,
      isPlatformPermissible: true,
      isV0Dataset: false,
      isV1Dataset: false,
      rgifExclusiveCompany: true,
      version: 1,
      companyMatchFile: undefined as MatchFileConfig | undefined,
    },
    version: 2,
    title: "Opportunity Generator",
    installInclusion: "NONE",
    timeSeriesInclusion: "NONE",
    intentInclusion: "NONE",
    faiInclusion: "NONE",
    contractsInclusion: "NONE",
    mentionInclusion: "NONE",
    mentionTimeSeriesInclusion: "NONE",
    spendInclusion: "ALL",
    aiSpendInclusion: "NONE",
    relativeSpendInclusion: "NONE",
    generativeAIInclusion: "NONE",
    rowBasedSpendInclusion: "NONE",
    installProductIds: undefined,
    faiProductIds: undefined,
    includeDecisionMakersAndInfluencersOnly: undefined,
    columns: {
      spend: {
        productPresence: [] as ProductPresenceColumn[],
        vendorPresence: [] as VendorPresenceColumn[],
      },
    },
    isCloudMaturityEnabled: false,
    enableFetchDataFromS3: false,
    enableUseHermesApi: true,
    enableNewFaiSheet: true,
  },
  scope:
    "access:export access:spend-view access:contract-view access:install-view",
};

const buildInclusionParams = (
  exportInclusions: ExportInclusionData,
  includeDecisionMakersAndInfluencersOnly?: boolean
) => {
  const params: Partial<ExportPayload["params"]> = {};

  const {
    installProductIds,
    faiProductIds,
    topicIds,
    intentSignalScore,
    ...inclusionsWithoutConditionalParams
  } = exportInclusions;

  // Add install product ids if the install inclusion is SELECTED
  if (
    exportInclusions.installInclusion === EXPORT_INCLUSION_TYPES.SELECTED &&
    (installProductIds || []).length > 0
  ) {
    params.installProductIds = installProductIds;
  }

  // Add fai product ids if the fai inclusion is SELECTED
  if (
    exportInclusions.faiInclusion === EXPORT_INCLUSION_TYPES.SELECTED &&
    (faiProductIds || []).length > 0
  ) {
    params.faiProductIds = faiProductIds;
  }

  // Add intent topic ids only when intent inclusion is SELECTED
  if (
    exportInclusions.intentInclusion === EXPORT_INCLUSION_TYPES.SELECTED &&
    (topicIds || []).length > 0
  ) {
    params.topicIds = topicIds;
  }

  // Transform intentSignalScore: ALL => null, others => as-is
  if (
    exportInclusions.intentInclusion &&
    exportInclusions.intentInclusion !== EXPORT_INCLUSION_TYPES.NONE
  ) {
    if (intentSignalScore === "ALL") {
      params.intentSignalScore = null;
    } else if (intentSignalScore !== undefined) {
      params.intentSignalScore = intentSignalScore;
    }
  }

  // Add decision makers and influencers flag on root level only if the fai inclusion is ALL or SELECTED
  if (
    [EXPORT_INCLUSION_TYPES.ALL, EXPORT_INCLUSION_TYPES.SELECTED].includes(
      exportInclusions.faiInclusion
    )
  ) {
    params.includeDecisionMakersAndInfluencersOnly =
      !!includeDecisionMakersAndInfluencersOnly;
  }

  return { ...inclusionsWithoutConditionalParams, ...params };
};

export const getExportPayload = (
  profile: UserProfile,
  exportInclusions: ExportInclusionData,
  localizeInstalls: boolean = true,
  filters: CompanyFilter,
  customColumns?: {
    productPresence: ProductPresenceColumn[];
    vendorPresence: VendorPresenceColumn[];
  },
  isCloudMaturityEnabled?: boolean,
  extraFilters: ExtraSearchFilters = {},
  workspaceWhitespace?: MatchFileConfig,
  scoringProfiles?: OpportunityGeneratorScoringProfileSalesplaySearch[],
  activeSortEntry?: { field: string; direction: string },
  activeModels: Array<{ family: string; name: string }> = [],
  modelScoreFilter?: ModelScoreFilterInput[],
  companyConfig?: {
    isPlatformPermissible: boolean;
    isV0Dataset: boolean;
    isV1Dataset: boolean;
    rgifExclusiveCompany: boolean;
  }
) => {
  const payload: ExportPayload = structuredClone(baseExportPayload);
  payload.profile = profile;
  payload.frontendDomain =
    import.meta.env.VITE_FRONTEND_DOMAIN || window.location.origin;
  payload.params.searchParams.localizeInstalls = localizeInstalls;
  // Scoring profiles and the "Fit Score Normalized-{index}" (or "Intent Score
  // Normalized-{index}") sort sentinel must be sent together — the backend
  // uses the sentinel's trailing index to select which profile's criteria to
  // apply. Sending profiles without the sentinel triggers Integer.parse(nil)
  // and returns {:error, :invalid_payload}. When the user is not actively
  // sorting by a score column, the profiles are not meaningful for the
  // estimate, so we omit both.
  const isScoreSort = (field: string) =>
    field.startsWith("Fit Score Normalized") ||
    field.startsWith("Intent Score Normalized");

  if (
    scoringProfiles &&
    scoringProfiles.length > 0 &&
    activeSortEntry &&
    isScoreSort(activeSortEntry.field)
  ) {
    payload.params.searchParams.scoringProfiles = scoringProfiles;
    payload.params.searchParams.sort = [
      {
        field: activeSortEntry.field,
        direction: activeSortEntry.direction.toUpperCase(),
      },
      ...payload.params.searchParams.sort,
    ];
  } else {
    payload.params.searchParams.scoringProfiles = [];
  }

  payload.params.searchParams.filters = {
    ...filters,
    ...extraFilters,
  };
  payload.params.isCloudMaturityEnabled = !!isCloudMaturityEnabled;

  // Company Configuration (Tier Selection, RGIF Companies, V0) — ported from
  // OppGen's TiersAndRgifSettings, whose values ride on every export's
  // searchParams. Overrides the baseExportPayload defaults when given.
  if (companyConfig) {
    payload.params.searchParams.isPlatformPermissible =
      companyConfig.isPlatformPermissible;
    payload.params.searchParams.isV0Dataset = companyConfig.isV0Dataset;
    payload.params.searchParams.isV1Dataset = companyConfig.isV1Dataset;
    payload.params.searchParams.rgifExclusiveCompany =
      companyConfig.rgifExclusiveCompany;
  }

  // Add companyMatchFile if opportunity type filter is present and whitespace is available
  if (
    filters?.opportunityType &&
    workspaceWhitespace?.fileId &&
    workspaceWhitespace?.lastMatched
  ) {
    payload.params.searchParams.companyMatchFile = workspaceWhitespace;
  }

  const inclusionParams = buildInclusionParams(
    exportInclusions,
    extraFilters.includeDecisionMakersAndInfluencersOnly
  );

  Object.assign(payload.params, inclusionParams);

  // Update custom columns if provided
  if (customColumns) {
    payload.params.columns.spend.productPresence =
      customColumns.productPresence;
    payload.params.columns.spend.vendorPresence = customColumns.vendorPresence;
  }

  if (activeModels.length > 0) {
    payload.params.searchParams.model_scores = activeModels.map(
      ({ family, name }) => ({
        model_id: family,
        name,
        columns: ["composite", "fit", "need", "intent", "tier"],
      })
    );
    const tenantId = getTenantId();
    if (tenantId) {
      payload.params.searchParams.tenant_id = tenantId;
    }
  }

  if (modelScoreFilter && modelScoreFilter.length > 0) {
    payload.params.searchParams.model_score_filter = modelScoreFilter.map(
      ({ modelId, column, min, max, eq, in: inValues }) => ({
        model_id: modelId,
        column: column.toLowerCase(),
        ...(min !== undefined && min !== null ? { min } : {}),
        ...(max !== undefined && max !== null ? { max } : {}),
        ...(eq !== undefined && eq !== null ? { eq } : {}),
        ...(inValues && inValues.length > 0
          ? {
              in: inValues.filter(
                (v): v is string => v !== null && v !== undefined
              ),
            }
          : {}),
      })
    );
  }

  return payload;
};
